import React from 'react';
import moment from 'moment';
import {Field, FieldsManager} from '../../../../data/services/fields';
import {fillFieldsFromData} from '../../../../common/util/util-fields';
import {classNames, getDefaultContactGroup, getDefaultUserOfficeValues} from '../../../../common/util/util-helpers';
import {numberWithCommas, numberWithCommasToBack} from '../../../../util/util-formaters';
import {dirtyClone} from '../../../../common/util/util-vanilla';
import Resources from '../../../../data/services/resources';
import BillExpenseOcrItemsTable from './bill-expense-ocr-items-table';
import ButtonsGroup from '../../../../common/components/buttons/buttons-group';
import {getCustomStyles} from '../../../../data/themes';
import FieldsForm from '../../../../common/components/fields/fields-form';
import InfoParagraph from '../../../../common/components/info-paragraph';
import {CHARGE_TO_COMPANY, chargeToFieldState, chargeToFromItem, chargeToOptions} from '../charge-to';

// Every label rendered from these field templates comes from the host translate. The templates and the
// validation below are also driven from the controller (state lives there so it survives the shell's
// panel toggles), so translate is a parameter rather than a closure — degrade to the raw key instead
// of throwing.
const fallbackTranslate = (key) => key;

function toDateValue(dateString) {
    return dateString ? moment(dateString).format('YYYY-MM-DD HH:mm:ss') : moment().format('YYYY-MM-DD HH:mm:ss');
}

// Scoped ~40px input height/padding for this form's header fields only — react-select based
// fields (select-search + select, both backed by react-select under the hood) read `inputStyles`
// which fully replaces getCustomStyles(), so the base styles are spread back in here.
function getLargeFieldStyles() {
    const base = getCustomStyles();

    return {
        ...base,
        control: (provided, state) => ({
            ...base.control(provided, state),
            minHeight: '40px',
        }),
        valueContainer: (provided) => ({
            ...base.valueContainer(provided),
            padding: '0.625rem 0.75rem',
        }),
    };
}

export function getHeaderFields(data, vendorMatch, translate = fallbackTranslate) {
    const chargeToOrganization = vendorMatch?.type === 'organization';
    const chargeToContact = vendorMatch?.type === 'contact';

    return {
        // This is who the bill is paid to, not a line's charge-to — the same thing the
        // income/expense dialog labels "Pay To" on a bill.
        ChargeTo: new Field('ChargeTo', chargeToContact ? '1' : '2', [''], false, 'button-group', {
            label: 'PayTo',
            data: {
                1: translate('label.ChargeToContactID'),
                2: translate('label.ChargeToOrganizationID'),
            },
            addContainerClass: 'col-span-full',
        }),
        // Exactly the side ChargeTo points at is editable and required; the other is
        // disabled so a bill cannot end up naming both. handleInputChange holds the
        // invariant on switch.
        ContactID: new Field(
            'ContactID',
            chargeToContact
                ? {label: `${vendorMatch.match.FirstName} ${vendorMatch.match.LastName}`, value: vendorMatch.match.ContactID, metadata: vendorMatch.match}
                : '',
            [chargeToContact ? 'empty' : ''],
            !chargeToContact,
            'select-search',
            {addContainerClass: 'col-span-12 sm:col-span-6', inputStyles: getLargeFieldStyles()}
        ),
        OrganizationID: new Field(
            'OrganizationID',
            chargeToOrganization
                ? {label: vendorMatch.match.LegalName, value: vendorMatch.match.OrganizationID, metadata: vendorMatch.match}
                : '',
            [chargeToContact ? '' : 'empty'],
            chargeToContact,
            'select-search',
            {addContainerClass: 'col-span-12 sm:col-span-6', inputStyles: getLargeFieldStyles()}
        ),
        OfficeID: new Field(
            'OfficeID',
            getDefaultUserOfficeValues(),
            ['empty'],
            false,
            'select-search',
            {addContainerClass: 'col-span-12 sm:col-span-6 lg:col-span-4', inputStyles: getLargeFieldStyles()}
        ),
        ContactGroupID: new Field(
            'ContactGroupID',
            getDefaultContactGroup(),
            ['empty'],
            false,
            'select-search',
            {addContainerClass: 'col-span-12 sm:col-span-6 lg:col-span-4', inputStyles: getLargeFieldStyles()}
        ),
        PaymentTypeID: new Field(
            'PaymentTypeID',
            '',
            ['empty'],
            false,
            'select',
            {addContainerClass: 'col-span-12 sm:col-span-6', inputStyles: getLargeFieldStyles()}
        ),
        AccountID: new Field(
            'AccountID',
            '',
            ['empty'],
            false,
            'select-search',
            {addContainerClass: 'col-span-12 sm:col-span-6', inputStyles: getLargeFieldStyles()}
        ),
        Date: new Field(
            'Date',
            toDateValue(data?.Date),
            ['empty'],
            false,
            'date',
            {addContainerClass: 'col-span-12 sm:col-span-6 lg:col-span-4', addClass: 'h-10 py-2.5 px-3 text-sm'}
        ),
        BookDate: new Field(
            'BookDate',
            toDateValue(data?.BookDate),
            ['empty'],
            false,
            'date',
            {addContainerClass: 'col-span-12 sm:col-span-6 lg:col-span-4', addClass: 'h-10 py-2.5 px-3 text-sm'}
        ),
        DueDate: new Field(
            'DueDate',
            toDateValue(data?.DueDate),
            ['empty'],
            false,
            'date',
            {addContainerClass: 'col-span-12 sm:col-span-6 lg:col-span-4', addClass: 'h-10 py-2.5 px-3 text-sm'}
        ),
        RefNumber: new Field(
            'RefNumber',
            data?.RefNumber ?? '',
            ['empty'],
            false,
            'text',
            {addContainerClass: 'col-span-12 sm:col-span-6 lg:col-span-4', addClass: 'h-10 py-2.5 px-3 text-sm'}
        ),
        InternalNotes: new Field('InternalNotes', data?.Notes ?? '', [''], false, data?.Notes ? 'textarea' : 'hidden', {addContainerClass: 'col-span-full'}),
    };
}

export function getDefaultAccount(organizationMetadata, defaults) {
    if (organizationMetadata?.DefaultPayableAccountID) {
        return {AccountID: organizationMetadata.DefaultPayableAccountID, Account: organizationMetadata.DefaultPayableAccount};
    }

    if (defaults?.ExpenseAccountID) {
        return {AccountID: defaults.ExpenseAccountID, Account: defaults.ExpenseAccount};
    }

    return null;
}

/**
 * Point the Field map at one charge-to option: set the discriminator and show/require the
 * matching FK. Derived from the stored IDs rather than any saved flag — ChargeTo is not a
 * column. Not shared with charge-to-apply.js because nothing here needs FieldsManager.
 */
function withChargeToState(fields, chargeTo) {
    fields.ChargeTo.value = chargeTo;

    Object.entries(chargeToFieldState(chargeTo)).forEach(([name, state]) => {
        fields[name].type = state.type;
        fields[name].validate = state.validate;
    });

    return fields;
}

export function getExpenseItemField(item = {}, translate = fallbackTranslate, defaultAccount = null) {
    const fieldTemplates = {
        ProductServiceID: new Field('ProductServiceID', '', [''], false, 'select-search', {addContainerClass: 'col-span-full'}),
        Description: new Field('Description', '', ['empty'], false, 'textarea', {addContainerClass: 'col-span-full'}),
        AccountID: new Field('AccountID', defaultAccount ? {label: defaultAccount.Account, value: defaultAccount.AccountID} : '', ['empty'], false, 'select-search', {addContainerClass: 'col-span-full'}),
        Amount: new Field('Amount', '', ['money'], false, 'money', {addContainerClass: 'col-span-full'}, {isNegativeAllowed: true}),
        Qty: new Field('Qty', 1, ['required', 'numeric', 'non-zero'], false, 'text', {addContainerClass: 'col-span-full'}),
        TaxID: new Field('TaxID', '', [''], false, 'select-search', {addContainerClass: 'col-span-full'}),
        TotalAmount: new Field('TotalAmount', 0, [], true, 'money', {addContainerClass: 'col-span-full'}, {isNegativeAllowed: true}),

        // Charge To is part of every bill line here, imported or AI-extracted — same control
        // and same option set as regular bill entry. Number, not string: compared with
        // strict === against the CHARGE_TO_* values.
        ChargeTo: new Field('ChargeTo', CHARGE_TO_COMPANY, [''], false, 'button-group', {
            data: chargeToOptions(translate, {allowTruck: true}),
            hideTable: true,
            addContainerClass: 'col-span-full',
        }),
        ChargeToContactID: new Field('ChargeToContactID', '', [], false, 'hidden', {
            addContainerClass: 'col-span-full',
        }, {isClearable: true}),
        ChargeToOrganizationID: new Field('ChargeToOrganizationID', '', [], false, 'hidden', {
            addContainerClass: 'col-span-full',
        }, {isClearable: true}),
        ChargeToTruckID: new Field('ChargeToTruckID', '', [], false, 'hidden', {
            addContainerClass: 'col-span-full',
        }, {isClearable: true}),
        ProductServiceTypeID: new Field('ProductServiceTypeID', '', [], true, 'hidden', {hideTable: true}),
    };

    // Import-specific, and genuinely not columns: the name off the ticket and why the
    // server could not match it, kept on the line so the warning can say which one needs
    // attention. AI-extracted lines have neither.
    if ('MatchWarning' in item) {
        fieldTemplates.MatchName = new Field('MatchName', '', [], false, 'hidden', {hideTable: true, addContainerClass: 'col-span-full'});
        fieldTemplates.MatchWarning = new Field('MatchWarning', '', [], false, 'hidden', {hideTable: true, addContainerClass: 'col-span-full'});
    }

    // isClearable twice on purpose: FieldsForm spreads `field.props`, while fieldsToHtml
    // (behind the items table's edit modal) spreads the Field itself and
    // FieldDropdownSelect reads a top-level `isClearable`. Either alone leaves one of the
    // two dialogs' selects unclearable.
    ['ChargeToContactID', 'ChargeToOrganizationID', 'ChargeToTruckID'].forEach((name) => {
        fieldTemplates[name].isClearable = true;
    });

    // Both callers hand in a FLATTENED row (raw `<Name>ID` scalars plus separate `<Name>` label keys):
    // the OCR pre-fill below, and InvoiceExpenseItemTable's edit modal, which passes the display row
    // straight from its getFormattedFields(). fillFieldsFromData is what reconstructs the
    // {label, value} objects the select-search fields need out of those key pairs.
    if (Object.keys(item).length > 0) {
        // Charge-to types first: fillFieldsFromData builds {value, label} only for
        // select-search fields, so filling while the FKs are still 'hidden' leaves a bare ID
        // with no label and the line reads as charged to the company.
        const chargeTo = chargeToFromItem(item);
        withChargeToState(fieldTemplates, chargeTo);

        const fieldsUpdate = fillFieldsFromData(fieldTemplates, item);

        // fillFieldsFromData rebuilds only {value, label} — the tax multiplier has to be re-attached.
        if (item?.TaxAmount && fieldsUpdate.TaxID.value) {
            fieldsUpdate.TaxID.value.amount = item.TaxAmount;
        }

        return withChargeToState(fieldsUpdate, chargeTo);
    }

    return withChargeToState(fieldTemplates, CHARGE_TO_COMPANY);
}

export function itemsFromExtraction(extractedItems, translate = fallbackTranslate, defaultAccount = null) {
    return (extractedItems ?? []).map((it) => {
        const matchedProductService = it.matchedProductService;
        const matchedTax = it.matchedTax;

        // The AI extraction sometimes returns a line's TotalAmount without a usable per-unit
        // Amount (e.g. a flat fee with Qty 0, or a rate embedded only in the description text).
        // Qty is a required/non-zero field downstream and the items table recomputes
        // TotalAmount as Qty * Amount * Tax the moment any of those fields changes, so leaving
        // Amount at 0 would either block submission (Qty 0) or silently zero out a correct
        // total on the next edit. Fold the original Qty into the Description for context and
        // normalize to Qty 1 / Amount = TotalAmount so the row is valid and arithmetically
        // stable from the start.
        const extractedAmount = Number(it.Amount) || 0;
        const extractedQty = Number(it.Qty) || 0;
        const totalAmount = Number(it.TotalAmount) || 0;
        const needsAmountFallback = extractedAmount === 0 && totalAmount !== 0;
        const description = needsAmountFallback && extractedQty > 1
            ? `(${it.Qty}) ${it.Description ?? ''}`.trim()
            : (it.Description ?? '');

        return getExpenseItemField({
            ProductServiceID: matchedProductService?.ProductServiceID,
            ProductService: matchedProductService?.ProductService,
            AccountID: matchedProductService?.ExpenseAccountID ?? defaultAccount?.AccountID,
            Account: matchedProductService?.ExpenseAccount ?? defaultAccount?.Account,
            Description: description,
            Amount: needsAmountFallback ? totalAmount : extractedAmount,
            Qty: needsAmountFallback ? 1 : (it.Qty ?? 1),
            TaxID: matchedTax?.TaxID,
            Tax: matchedTax?.Tax,
            TaxAmount: matchedTax?.Amount,
            TotalAmount: it.TotalAmount ?? 0,
        }, translate, defaultAccount);
    });
}

/**
 * Item keys that exist only to drive this dialog's UI. tbl_ExpenseLine has no column for
 * any of them, so they are dropped on the way to the bill.
 */
export const UI_ONLY_ITEM_KEYS = ['MatchName', 'MatchWarning'];

function toPostedItem(itemFields) {
    const values = FieldsManager.getFieldKeyValues(itemFields);
    UI_ONLY_ITEM_KEYS.forEach((key) => delete values[key]);

    return Object.assign({}, values, {
        Amount: numberWithCommasToBack(itemFields.Amount.value),
        TotalAmount: numberWithCommasToBack(itemFields.TotalAmount.value),
    });
}

export function validateOcrForm(mode, fields, items, translate = fallbackTranslate) {
    if (!fields || !items) {
        return {fields, items, errorMessage: null, result: null};
    }

    const isBillMode = mode === 'bill';
    // Every field that renders with a required marker has to be in this list — anything
    // left out is validated by nobody, which is how RefNumber came to be marked required
    // and still save empty.
    const list = isBillMode
        ? ['OrganizationID', 'ContactID', 'RefNumber', 'Date', 'BookDate', 'DueDate', 'OfficeID', 'ContactGroupID']
        : ['OrganizationID', 'ContactID', 'RefNumber', 'PaymentTypeID', 'Date', 'AccountID', 'BookDate', 'OfficeID', 'ContactGroupID'];

    const validatedFields = FieldsManager.validateFields(fields, list);
    const validatedItems = items.map((it) => FieldsManager.validateFields(it));
    const itemsHaveErrors = validatedItems.map((it) => FieldsManager.checkFieldsForErrors(it)).includes(false);

    if (!FieldsManager.checkFieldsForErrors(validatedFields, list) || itemsHaveErrors) {
        return {
            fields: validatedFields,
            items: validatedItems,
            errorMessage: translate('text.pleaseCheckItems'),
            result: null,
        };
    }

    const total = dirtyClone(items).reduce((memo, it) => memo + numberWithCommasToBack(it.TotalAmount.value), 0);

    return {
        fields: validatedFields,
        items: validatedItems,
        errorMessage: null,
        result: {
            fields: Object.assign({}, FieldsManager.getFieldKeyValues(fields), {
                Items: items.map(toPostedItem),
                Amount: numberWithCommasToBack(numberWithCommas(Math.round(total * 100) / 100)),
            }),
            resource: isBillMode ? Resources.ExpenseBill : Resources.ExpenseExpense,
        },
    };
}

export default function BillExpenseOcrForm({
    mode,
    setMode,
    fields,
    setFields,
    items,
    setItems,
    errorMessage,
    translate,
    defaults,
}) {
    /** UI Events
     ================================================================= */
    function handleInputChange(name, value) {
        if (name === 'ChargeTo') {
            // The button group hands back a Number, the initial template value is a string.
            const isChargeToContact = Number(value) === 1;

            setFields((prev) => ({
                ...prev,
                ContactID: {...prev.ContactID, validate: [isChargeToContact ? 'empty' : ''], disabled: !isChargeToContact, value: '', errorMessage: ''},
                OrganizationID: {...prev.OrganizationID, validate: [isChargeToContact ? '' : 'empty'], disabled: isChargeToContact, value: '', errorMessage: ''},
                ChargeTo: {...prev.ChargeTo, value, errorMessage: ''},
            }));
            return;
        }

        setFields((prev) => {
            const updated = FieldsManager.updateField(prev, name, value);
            updated[name].errorMessage = '';
            return updated;
        });
    }

    function handleModeChange(name, value) {
        setMode(value);
    }

    /** UI Events items
     ================================================================= */
    function handleAddNewItem(params) {
        // params arrives as an already-complete map of Field instances (ModalSaveResource
        // getRawFields). The item table's inline-add button calls this with no arguments.
        setItems((prev) => [...prev, params ?? getExpenseItemField({}, translate)]);
    }

    function handleEditItem(params, index) {
        setItems((prev) => prev.map((it, i) => (i === index ? params : it)));
    }

    function handleRemoveItem(index) {
        setItems((prev) => prev.filter((_, i) => i !== index));
    }

    /** Helpers
     ================================================================= */
    function isBillMode() {
        return mode === 'bill';
    }

    function getItemFields(item = {}) {
        const organizationMetadata = fields.OrganizationID?.value?.metadata;
        return getExpenseItemField(item, translate, getDefaultAccount(organizationMetadata, defaults));
    }

    /** Render
     ================================================================= */
    if (!fields || !items) {
        return null;
    }

    const contactFieldsState = {
        ChargeTo: fields.ChargeTo,
        ContactID: fields.ContactID,
        OrganizationID: fields.OrganizationID,
        OfficeID: fields.OfficeID,
        ContactGroupID: fields.ContactGroupID,
        RefNumber: fields.RefNumber,
        PaymentTypeID: isBillMode() ? {...fields.PaymentTypeID, type: 'hidden'} : fields.PaymentTypeID,
        AccountID: isBillMode() ? {...fields.AccountID, type: 'hidden'} : fields.AccountID,
        InternalNotes: fields.InternalNotes,
    };

    const dateFieldsState = {
        Date: fields.Date,
        BookDate: fields.BookDate,
        DueDate: isBillMode() ? fields.DueDate : {...fields.DueDate, type: 'hidden'},
    };

    return (
        <div className="p-6 space-y-6 max-w-[900px] mx-auto">
            <div>
                <span className="label-stack">{translate('text.what_are_you_creating')}</span>

                <ButtonsGroup
                    name="mode"
                    data={{bill: translate('btn.bill'), expense: translate('btn.expense')}}
                    value={mode}
                    onChange={handleModeChange}
                    addClass="w-full mt-1"
                />
            </div>

            <div className="rounded-xl border border-tm-gray-200 p-6 space-y-4">
                <h3 className="text-xs font-medium uppercase tracking-wide text-tm-gray-500">{translate('text.Contact')}</h3>

                <div className={classNames('grid grid-cols-12 gap-4')}>
                    <FieldsForm
                        fieldsState={contactFieldsState}
                        onChange={handleInputChange}
                        translate={translate}
                        selects={{
                            OrganizationID: {api: 'api/' + Resources.OrganizationsQuick, searchMap: (it) => ({label: it.LegalName, value: it.OrganizationID, metadata: it})},
                            ContactID: {api: 'api/' + Resources.ContactsQuick, searchMap: (it) => ({label: `${it.FirstName} ${it.LastName}`, value: it.ContactID, metadata: it})},
                            OfficeID: {api: 'api/' + Resources.OfficesQuick, searchMap: (it) => ({label: it.OfficeName, value: it.OfficeID})},
                            ContactGroupID: {api: 'api/' + Resources.ContactGroupsQuick, searchMap: (it) => ({label: it.ContactGroupName, value: it.ContactGroupID, metadata: it})},
                            AccountID: {api: 'api/' + Resources.AccountsQuick, searchMap: (it) => ({label: it.AccountName, value: it.AccountID})},
                        }}
                    />
                </div>
            </div>

            <div className="rounded-xl border border-tm-gray-200 p-6 space-y-4">
                <h3 className="text-xs font-medium uppercase tracking-wide text-tm-gray-500">{translate('text.dates')}</h3>

                <div className={classNames('grid grid-cols-12 gap-4')}>
                    <FieldsForm
                        fieldsState={dateFieldsState}
                        onChange={handleInputChange}
                        translate={translate}
                    />
                </div>
            </div>

            <div className="rounded-xl border border-tm-gray-200 py-6">
                <BillExpenseOcrItemsTable
                    translate={translate}
                    items={items}
                    getFields={getItemFields}
                    onAddNewItem={handleAddNewItem}
                    onEditItem={handleEditItem}
                    onRemoveItem={handleRemoveItem}
                />

                {!!errorMessage && (
                    <div className="mt-2 px-6">
                        <InfoParagraph type="danger">
                            {errorMessage}
                        </InfoParagraph>
                    </div>
                )}
            </div>
        </div>
    );
}
